import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { GetglobalsettingsService } from './getglobalsettings.service';
import { AgentBankBlockedCounterparty } from '../shared/agent-bank-blocked-counterparties';

/**
 * REST client for banking integration, `/api/agent/bank-blocked-counterparties`
 * on the webapi backend — CRUD for `AgentBankBlockedCounterparties` (manual
 * counterparty block list). A separate service file per this repo's own
 * "one service per API area" convention — this is a different API area from
 * the bank-credentials endpoint.
 *
 * Every route is `realm:portal-admin` server-side; this service does not
 * itself gate access — the host component/page does. Errors are NOT caught/
 * alerted here — callers subscribe with their own `error` handler and
 * surface it via `MessageService` toast.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentBankBlockedCounterpartiesApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /** Builds an absolute API URL by prepending the runtime API server address. */
  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  /** Fresh standard headers per call so Setup-screen overrides take effect
   *  without a page reload. */
  private _options() {
    return { headers: this.globalsettingsservice.getStandardApiHeaders() };
  }

  /** `GET /api/agent/bank-blocked-counterparties` — full list, unfiltered,
   *  including inactive rows. */
  getBlockedCounterparties(): Observable<AgentBankBlockedCounterparty[]> {
    return this.http.get<AgentBankBlockedCounterparty[]>(
      this._getApiUrl('/api/agent/bank-blocked-counterparties'),
      this._options()
    ).pipe(retry(1));
  }

  /** `POST /api/agent/bank-blocked-counterparties` — insert a new row.
   *  Returns `{ id }`. */
  createBlockedCounterparty(row: Partial<AgentBankBlockedCounterparty>): Observable<{ id: number }> {
    const { id, added_by, added_at, uid, cloneIndex, ...body } = row as AgentBankBlockedCounterparty;
    return this.http.post<{ id: number }>(
      this._getApiUrl('/api/agent/bank-blocked-counterparties'),
      body,
      this._options()
    ).pipe(retry(1));
  }

  /** `PUT /api/agent/bank-blocked-counterparties/:id` — update an existing row.
   *  `added_by`, `added_at`, and `active` are server-derived read-only fields
   *  and are stripped before sending. `active` is toggled via the separate
   *  `setActive()` method, not here. */
  updateBlockedCounterparty(row: AgentBankBlockedCounterparty): Observable<{ id: number }> {
    const { id, added_by, added_at, active, uid, cloneIndex, ...body } = row;
    return this.http.put<{ id: number }>(
      this._getApiUrl('/api/agent/bank-blocked-counterparties/' + row.id),
      body,
      this._options()
    ).pipe(retry(1));
  }

  /** `PATCH /api/agent/bank-blocked-counterparties/:id/active` — toggle the
   *  `active` flag. This is a separate endpoint, not part of the main `PUT`
   *  save flow. */
  setActive(id: number, active: boolean): Observable<{ id: number }> {
    return this.http.patch<{ id: number }>(
      this._getApiUrl('/api/agent/bank-blocked-counterparties/' + id + '/active'),
      { active },
      this._options()
    );
  }
}
